import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Modal, Tooltip, message } from 'antd';
import { DisconnectOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import {
  BankMark,
  CashTypeIcon,
  ConnectedStatus,
  LiabilityMark,
  LinkedAccountsTable,
  linkedAccountFundsLabel,
  linkedAccountTypeLabel,
  linkedAccountsTotals,
} from '@wavvest/wealth-ui';
import type { LinkedAccountRow } from '@wavvest/wealth-ui';
import {
  deletePlaidConnection,
  deletePlaidConnectionByItemId,
  fetchBankUnlinkCheck,
} from '@/api/custom';
import type { TimeRange } from '@/api/custom';
import { fetchPlaidWealthSnapshot } from '@/store/features/plaid/plaid-thunks';
import { invalidateWealthData } from '@/store/features/wealth-data-cache';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectPlaidAccounts } from '@/store/features/plaid/plaid-selectors';
import { formatCurrency, selectPlaidWealthValue } from '@wavvest/shared-utils';
import { ASSET_LIABILITY } from '@/util/planning-util';
import { selectPlaidDisplayBalance } from '@/util/plaid-util';
import { formatAccountType } from '@/util/account-type-format';
import {
  BankLinkWallet,
  formatExternalBankAccountTitle,
  fundingChipAccountKind,
} from '@/util/bank-link';
import { getAccountFundingPlaidId } from './deposit-account-selection';
import { requestPlaidLink } from './plaid-link-request';
import {
  accountBelongsToBank,
  apexAccountNames,
  apexAccountsText,
  apexRemovalNotice,
  bankMatchTokens,
  bankPlaidIds,
  groupBankConnections,
  pendingTransferReason,
} from './bank-connections';
import type { BankConnection, BankUnlinkCheckState } from './bank-connections';
import UnlinkBankModal from './UnlinkBankModal';
import type { BankAccountData } from '@/util/plaid-util';

type LinkedAccountsSectionProps = {
  wallets: readonly BankLinkWallet[];
  /**
   * Whether the investor has any Apex account, legacy or picker-managed. `wallets`
   * only holds picker-managed ones, so it cannot answer this on its own. Falls back
   * to `wallets` when the page does not say.
   */
  hasApexAccounts?: boolean;
  /** Chart range on screen, so a refresh after unlinking keeps the same range. */
  chartRange?: TimeRange;
};

const isApex = (account: BankAccountData) => account.custodian_code === 'APEX';
const isLiability = (account: BankAccountData) =>
  account.asset_or_liability === ASSET_LIABILITY.LIABILITY;
const isCash = (account: BankAccountData) => !isApex(account) && !isLiability(account);

const accountTitle = (account: BankAccountData) =>
  formatExternalBankAccountTitle({
    institution_name: account.institution_name,
    name: account.name,
    account_type: account.account_type,
  });

const LinkedAccountsSection: React.FC<LinkedAccountsSectionProps> = ({
  wallets,
  hasApexAccounts: hasApexAccountsProp,
  chartRange = 'ALL',
}) => {
  const dispatch = useAppDispatch();
  const accounts = useAppSelector(selectPlaidAccounts);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<string, BankUnlinkCheckState>>({});
  // Set synchronously so a fast double click cannot open two confirmations before
  // the first has rendered. Released when the confirmation closes.
  const unlinkConfirmOpen = useRef(false);
  const cash = useMemo(() => accounts.filter(isCash), [accounts]);
  const liabilities = useMemo(() => accounts.filter(isLiability), [accounts]);
  const cashTotal = cash.reduce((sum, account) => sum + selectPlaidWealthValue(account), 0);
  const owedTotal = liabilities.reduce((sum, account) => sum + selectPlaidWealthValue(account), 0);

  const fundedCount = (account: BankAccountData) => {
    const plaidId = getAccountFundingPlaidId(account);
    return wallets.filter((wallet) => wallet.current_link?.plaid_id === plaidId).length;
  };

  const refresh = () => dispatch(fetchPlaidWealthSnapshot({ range: chartRange })).unwrap();

  // Plaid can only remove a whole bank login, so unlinking works per bank and
  // every account under it goes together.
  const banks = groupBankConnections(accounts.filter((account) => !isApex(account)));
  const apexAccountName = useMemo(() => apexAccountNames(accounts, wallets), [accounts, wallets]);
  // Apex only has something to say about a bank when the investor has an Apex account.
  const hasApexAccounts = hasApexAccountsProp ?? wallets.length > 0;
  const bankKeys = banks.map((bank) => bank.key).join('|');

  // When the panel opens, ask Apex what unlinking each bank would do: which Apex
  // accounts lose it as a funding bank, and whether a transfer is still in flight.
  useEffect(() => {
    if (!unlinkOpen || !hasApexAccounts) {
      // Drop answers that no longer apply, so a closed panel cannot show them again.
      setChecks((current) => (Object.keys(current).length > 0 ? {} : current));
      return undefined;
    }
    let cancelled = false;
    setChecks(Object.fromEntries(banks.map((bank) => [bank.key, { status: 'loading' as const }])));
    banks.forEach((bank) => {
      fetchBankUnlinkCheck(bankPlaidIds(bank))
        .then((check) => ({ status: 'ready' as const, check }))
        .catch((error) => {
          console.error('Failed to check bank before unlinking', error);
          return { status: 'error' as const };
        })
        .then((state) => {
          if (!cancelled) {
            setChecks((current) => ({ ...current, [bank.key]: state }));
          }
        });
    });
    return () => {
      cancelled = true;
    };
    // `banks` is rebuilt every render; its keys are what should re-run the checks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlinkOpen, hasApexAccounts, bankKeys]);

  // Unlink by item id (ENG-2425). The access token route is only a fallback for
  // legacy rows whose item_id is empty; a bank with neither has canUnlink false and
  // its button is disabled, so this is never reached without one of them.
  const removeConnection = async (bank: BankConnection): Promise<unknown> => {
    if (bank.itemId) {
      const result = await deletePlaidConnectionByItemId(bank.itemId);
      // A 2xx that removed nothing means the backend didn't find the Item for this
      // user; the accounts are still there, so this is not a successful unlink.
      if (result?.removed_count === 0) {
        throw new Error(`Unlinking item ${bank.itemId} removed no accounts`);
      }
      return result;
    }
    if (bank.accessToken) {
      return deletePlaidConnection(bank.accessToken);
    }
    throw new Error('This bank has no item id or token to unlink by');
  };

  const unlinkBank = (bank: BankConnection) => {
    if (!bank.canUnlink || unlinkConfirmOpen.current) {
      return;
    }
    unlinkConfirmOpen.current = true;
    const name = bank.institutionName;
    const count = bank.accounts.length;
    const apexNotice = apexRemovalNotice(bank, checks[bank.key], apexAccountName);
    Modal.confirm({
      title: `Unlink ${name}?`,
      content: (
        <>
          <p>
            {`${bank.accounts.map(accountTitle).join(', ')} will be removed, and you'll stop seeing ${count === 1 ? 'its balance' : 'their balances'}. You can link ${name} again later.`}
          </p>
          {apexNotice ? <p>{apexNotice}</p> : null}
        </>
      ),
      okText: `Unlink ${name}`,
      okButtonProps: { danger: true },
      afterClose: () => {
        unlinkConfirmOpen.current = false;
      },
      onOk: async () => {
        setPendingKey(bank.key);
        let fundedApexAccountIds: string[] = [];
        if (hasApexAccounts) {
          // Check again right before removing: a transfer may have started since the
          // panel opened, and removing the bank mid-transfer is what this guards against.
          try {
            const fresh = await fetchBankUnlinkCheck(bankPlaidIds(bank));
            if (!fresh.can_unlink) {
              setChecks((current) => ({
                ...current,
                [bank.key]: { status: 'ready', check: fresh },
              }));
              message.error(pendingTransferReason(name));
              setPendingKey(null);
              return;
            }
            fundedApexAccountIds = fresh.affected_accounts.map((account) => account.account_id);
          } catch (error) {
            console.error('Failed to check bank before unlinking', error);
            message.error("We couldn't check for transfers in progress. Please try again.");
            setPendingKey(null);
            return;
          }
        }
        try {
          await removeConnection(bank);
        } catch (error) {
          console.error('Failed to unlink Plaid connection', error);
          message.error(`We couldn't unlink ${name}. Please try again.`);
          setPendingKey(null);
          return;
        }
        // Linked accounts changed, so the next Wealth visit reloads them.
        dispatch(invalidateWealthData());
        // A 2xx is not proof every account went away: a legacy token-only row grouped
        // in via a shared token is not removed by the item-id delete. Check the
        // refreshed accounts before reporting success, matching on the item id or
        // any token this bank was seen with.
        const removed = `Unlinked ${name} (${count} ${count === 1 ? 'account' : 'accounts'})`;
        const fundingNote =
          fundedApexAccountIds.length > 0
            ? `. It's being removed as a funding bank for ${apexAccountsText(fundedApexAccountIds, apexAccountName)}.`
            : '';
        try {
          const snapshot = await refresh();
          const belongs = accountBelongsToBank({
            itemId: bank.itemId,
            matchTokens: bankMatchTokens(bank),
          });
          const remaining = (snapshot?.accounts ?? []).filter(belongs);
          if (remaining.length > 0) {
            console.error('Unlink reported success but accounts are still linked:', name);
            message.error(
              `We couldn't fully unlink ${name}. Still linked: ${remaining.map(accountTitle).join(', ')}. Please try again.`,
            );
          } else {
            message.success(`${removed}${fundingNote}`);
          }
        } catch (error) {
          // The delete itself succeeded, so don't call it a failure; but without the
          // refreshed accounts it can't be confirmed either.
          console.error('Failed to refresh balances after unlink', error);
          message.warning(
            `${removed}${fundingNote || '.'} Your balances will update on the next refresh.`,
          );
        } finally {
          setPendingKey(null);
        }
      },
    });
  };

  const menu = (account: BankAccountData) => ({
    items: [
      {
        key: 'refresh',
        label: 'Refresh balance',
        onClick: () => {
          refresh().catch((error) => {
            console.error('Failed to refresh linked account balances', error);
            message.error("We couldn't refresh your balances. Try again.");
          });
        },
      },
      {
        key: 'details',
        label: 'View details',
        onClick: () =>
          Modal.info({
            title: accountTitle(account),
            content: `${formatAccountType(account.account_type) || 'Account'}${
              account.mask ? ` ••${account.mask}` : ''
            } · ${formatCurrency(selectPlaidDisplayBalance(account))}`,
          }),
      },
    ],
  });

  const row = (account: BankAccountData, kind: 'cash' | 'liability'): LinkedAccountRow => {
    const title = accountTitle(account);
    const accountType = formatAccountType(account.account_type);
    const funded = fundedCount(account);
    return {
      id: account.account_id,
      title: (
        <Tooltip title={title}>
          <span>{title}</span>
        </Tooltip>
      ),
      mark:
        kind === 'cash' ? (
          <BankMark logo={account.institution_logo} name={account.institution_name || title} />
        ) : (
          <LiabilityMark />
        ),
      type: (
        <>
          {kind === 'cash' && (
            <CashTypeIcon savings={fundingChipAccountKind(accountType) === 'savings'} />
          )}
          {linkedAccountTypeLabel(accountType, account.mask)}
        </>
      ),
      amount: formatCurrency(selectPlaidDisplayBalance(account)),
      funds: kind === 'cash' ? linkedAccountFundsLabel(funded) : undefined,
      status: <ConnectedStatus />,
      actions: (
        <Dropdown menu={menu(account)} trigger={['click']}>
          <button
            type="button"
            aria-label={`Actions for ${title}`}
            className="linked-accounts-more"
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      ),
    };
  };

  const linkButton = (
    <Button type="link" icon={<PlusOutlined />} onClick={requestPlaidLink}>
      Link external account
    </Button>
  );
  const hasAccounts = cash.length > 0 || liabilities.length > 0;
  const totals = linkedAccountsTotals(cashTotal, owedTotal);
  const unlinkButton = (
    <Button type="link" danger icon={<DisconnectOutlined />} onClick={() => setUnlinkOpen(true)}>
      Unlink external bank
    </Button>
  );
  const footer = (
    <div className="linked-accounts-actions">
      {linkButton}
      {banks.length > 0 ? unlinkButton : null}
    </div>
  );

  return (
    <>
      <LinkedAccountsTable
        netTotal={hasAccounts ? totals.netTotal : undefined}
        cashLine={totals.cashLine}
        cashTotal={totals.cashTotal}
        cashRows={cash.map((account) => row(account, 'cash'))}
        liabilityTotal={totals.liabilityTotal}
        liabilityRows={liabilities.map((account) => row(account, 'liability'))}
        footer={hasAccounts ? footer : undefined}
        empty={linkButton}
      />
      <UnlinkBankModal
        open={unlinkOpen && banks.length > 0}
        banks={banks}
        checks={checks}
        apexAccountName={apexAccountName}
        pendingKey={pendingKey}
        onClose={() => setUnlinkOpen(false)}
        onUnlink={unlinkBank}
      />
    </>
  );
};

export default LinkedAccountsSection;
